import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicCustomAdventures, type CustomAdventure } from '@/utils/businessAppApi';
import { toast } from 'sonner';

export const useAdventuresData = (businessId: string | undefined) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });
  const [adventures, setAdventures] = useState<CustomAdventure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageReady, setPageReady] = useState(false);

  const previousBusinessId = useRef<string | undefined>(undefined);
  const hasLoadedData = useRef(false); // Prevent double loading in StrictMode

  // Load adventures
  useEffect(() => {
    const loadAdventures = async () => {
      if (!businessId) {
        setError(t('adventures.error.description'));
        setLoading(false);
        return;
      }

      // Reset loading flag when business changes
      if (previousBusinessId.current !== businessId) {
        hasLoadedData.current = false;
        previousBusinessId.current = businessId;
      }

      // Prevent double loading in React StrictMode
      if (hasLoadedData.current) {
        return;
      }
      hasLoadedData.current = true;

      try {
        setLoading(true);
        setError(null);
        const response = await getPublicCustomAdventures(businessId);
        
        if (!response || !Array.isArray(response)) {
          throw new Error('Invalid response format');
        }

        setAdventures(response);
        setPageReady(true);
      } catch (error) {
        console.error('Error loading adventures:', error);
        setError(t('adventures.error.description'));
        toast.error(t('adventures.error.description'));
      } finally {
        setLoading(false);
      }
    };

    loadAdventures();
  }, [businessId, t]);

  // Handle refresh function
  const handleRefresh = useCallback(async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      const response = await getPublicCustomAdventures(businessId);
      if (response && Array.isArray(response)) {
        setAdventures(response);
        toast.success(t('adventures.refreshed'));
      }
    } catch (error) {
      console.error('Error refreshing adventures:', error);
      toast.error(t('adventures.error.description'));
    } finally {
      setLoading(false);
    }
  }, [businessId, t]);

  return {
    adventures,
    loading,
    error,
    pageReady,
    handleRefresh
  };
};
